import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios } from "../../utils/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ExternalLink, Edit2, Save, X, Database, Activity, Bot, Download, Upload, Trash2, Clock, Zap, Play, BarChart3, Settings2, Link, Brain, Sparkles, Cloud, FileText, Cpu } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_LINKS = {
  redis: {
    name: "REDIS",
    description: "An in-memory data store used for ultra-fast caching, session storage, queues, and real-time state management.",
    url: "https://console.upstash.com/redis/e253cc59-0fc9-4d34-857b-fa12860e6923/usage?period=3h&teamid=0",
    icon: "database"
  },
  langfuse: {
    name: "LANGFUSE",
    description: "An LLM observability platform that traces, logs, and evaluates AI prompts, responses, costs, and model performance.",
    url: "https://us.cloud.langfuse.com/project/cmk922izn012zad08z9d27bsv/dashboards/cmawln8k700xqad07000k1q8b",
    icon: "activity"
  }
};

// LLM Provider dashboard URLs
const LLM_PROVIDER_DASHBOARDS = [
  {
    value: "anthropic",
    label: "Anthropic (Claude)",
    url: "https://console.anthropic.com/settings/logs",
    icon: "brain",
    color: "orange"
  },
  {
    value: "openai",
    label: "OpenAI (GPT)",
    url: "https://platform.openai.com/logs",
    icon: "sparkle",
    color: "green"
  },
  {
    value: "google",
    label: "Google (Gemini)",
    url: "https://aistudio.google.com/usage?timeRange=last-7-days",
    icon: "cloud",
    color: "blue"
  },
  {
    value: "deepseek",
    label: "DeepSeek",
    url: "https://platform.deepseek.com/usage",
    icon: "cpu",
    color: "cyan"
  }
];

// Available LLM Models
const AVAILABLE_MODELS = [
  { value: "default", label: "Default (System)", provider: null, model: null },
  { value: "anthropic/claude-opus-4-6", label: "Claude Opus 4.6", provider: "anthropic", model: "claude-opus-4-6" },
  { value: "anthropic/claude-sonnet-4-6", label: "Claude Sonnet 4.6", provider: "anthropic", model: "claude-sonnet-4-6" },
  { value: "anthropic/claude-sonnet-4-5-20250929", label: "Claude Sonnet 4.5", provider: "anthropic", model: "claude-sonnet-4-5-20250929" },
  { value: "openai/gpt-5.2", label: "GPT-5.2", provider: "openai", model: "gpt-5.2" },
  { value: "openai/gpt-4o-mini", label: "GPT-4o-mini", provider: "openai", model: "gpt-4o-mini" },
  { value: "gemini/gemini-3-flash-preview", label: "Gemini 3 Flash", provider: "gemini", model: "gemini-3-flash-preview" },
  { value: "groq/llama-3.3-70b-versatile", label: "Llama 3.3 70B (Groq)", provider: "groq", model: "llama-3.3-70b-versatile" },
  { value: "groq/llama-3.1-8b-instant", label: "Llama 3.1 8B (Groq)", provider: "groq", model: "llama-3.1-8b-instant" },
  { value: "deepseek/deepseek-v4-pro", label: "DeepSeek V4 Pro (thinking)", provider: "deepseek", model: "deepseek-v4-pro" },
  { value: "deepseek/deepseek-v4-flash", label: "DeepSeek V4 Flash", provider: "deepseek", model: "deepseek-v4-flash" },
];

// All agents with their default LLM assignments
const AGENT_DEFAULTS = [
  // Persona Agents
  { id: "persona_voice", name: "PersonaVoiceAgent", category: "Persona", defaultLLM: "anthropic/claude-sonnet-4-5-20250929", description: "Generates persona responses" },
  { id: "persona_prompt_builder", name: "PersonaPromptBuilderAgent", category: "Persona", defaultLLM: "openai/gpt-5.2", description: "Builds persona system prompts" },
  { id: "coach_overlay", name: "CoachOverlayAgent", category: "Persona", defaultLLM: "openai/gpt-5.2", description: "Generates coaching annotations" },
  { id: "practice_feedback", name: "PracticeFeedbackAgent", category: "Persona", defaultLLM: "openai/gpt-5.2", description: "Evaluates practice conversations" },
  { id: "persona_orchestrator", name: "PersonaChatOrchestratorAgent", category: "Persona", defaultLLM: "openai/gpt-5.2", description: "Orchestrates persona chat flow" },
  
  // Core Agents
  { id: "emotion", name: "EmotionAgent", category: "Core", defaultLLM: "openai/gpt-5.2", description: "Analyzes emotional state" },
  { id: "stage", name: "StageAgent", category: "Core", defaultLLM: "openai/gpt-5.2", description: "Determines relationship stage" },
  { id: "plan_extractor", name: "PlanExtractorAgent", category: "Core", defaultLLM: "openai/gpt-5.2", description: "Extracts conversation plan" },
  { id: "mode_router", name: "ModeRouterAgent", category: "Core", defaultLLM: "openai/gpt-5.2", description: "Routes to appropriate mode" },
  { id: "ethics_guard", name: "EthicsGuardAgent", category: "Core", defaultLLM: "openai/gpt-5.2", description: "Monitors ethical boundaries" },
  { id: "risk_flagger", name: "RiskFlaggerAgent", category: "Core", defaultLLM: "openai/gpt-5.2", description: "Flags potential risks" },
  { id: "user_profile", name: "UserProfileAgent", category: "Core", defaultLLM: "openai/gpt-5.2", description: "Manages user profile context" },
  { id: "persona_profile", name: "PersonaProfileAgent", category: "Core", defaultLLM: "openai/gpt-5.2", description: "Manages persona profile" },
  
  // Profile Data Agents
  { id: "auto_data_entry", name: "AutoDataEntryAgent", category: "Profile Data", defaultLLM: "openai/gpt-4o-mini", description: "Extracts structured data from pasted text" },
  { id: "learning_profile_gen", name: "Learning Library Generator", category: "Profile Data", defaultLLM: "openai/gpt-4o-mini", description: "Generates 16 learning profile categories from data sources" },
  { id: "persona_generator", name: "AI Persona Generator", category: "Profile Data", defaultLLM: "gemini/gemini-3-flash-preview", description: "Generates complete persona profiles from user descriptions with seed/mesh blending" },
  
  // Analyzer Agents
  { id: "analyzer_router", name: "AnalyzerRouterAgent", category: "Analyzer", defaultLLM: "openai/gpt-4o-mini", description: "Routes analysis requests" },
  { id: "bio_rewrite", name: "BioRewriteAgent", category: "Analyzer", defaultLLM: "openai/gpt-5.2", description: "Rewrites social bios" },
  { id: "image_analysis", name: "ImageAnalysisAgent", category: "Analyzer", defaultLLM: "gemini/gemini-3-flash-preview", description: "Analyzes images" },
  { id: "social_commenter", name: "SocialCommenterAgent", category: "Analyzer", defaultLLM: "openai/gpt-5.2", description: "Generates social comments" },
  { id: "message_review", name: "MessageReviewAgent", category: "Analyzer", defaultLLM: "openai/gpt-5.2", description: "Reviews messages" },
  { id: "profile_audit", name: "ProfileAuditAgent", category: "Analyzer", defaultLLM: "openai/gpt-4o-mini", description: "Audits profiles" },
  
  // Planning Agents
  { id: "plan_alignment", name: "PlanAlignmentAgent", category: "Planning", defaultLLM: "openai/gpt-5.2", description: "Aligns conversation plans" },
  { id: "co_creation", name: "CoCreationAgent", category: "Planning", defaultLLM: "openai/gpt-5.2", description: "Co-creates strategies" },
  { id: "strategy", name: "StrategyAgent", category: "Planning", defaultLLM: "openai/gpt-5.2", description: "Develops strategies" },
  { id: "script", name: "ScriptAgent", category: "Planning", defaultLLM: "openai/gpt-5.2", description: "Creates response scripts" },
  { id: "tone", name: "ToneAgent", category: "Planning", defaultLLM: "openai/gpt-5.2", description: "Adjusts tone" },
  { id: "question_budget", name: "QuestionBudgetAgent", category: "Planning", defaultLLM: "openai/gpt-4o-mini", description: "Manages question budget" },
  
  // Memory Agents
  { id: "profile_extractor", name: "ProfileExtractorAgent", category: "Memory", defaultLLM: "openai/gpt-5.2", description: "Extracts profile data" },
  { id: "memory_admin", name: "MemoryAdminAgent", category: "Memory", defaultLLM: "openai/gpt-5.2", description: "Manages memory storage" },
  { id: "needs_confirmation", name: "NeedsConfirmationAgent", category: "Memory", defaultLLM: "openai/gpt-4o-mini", description: "Confirms information" },
  
  // Debrief Agents
  { id: "debrief_extractor", name: "DebriefExtractorAgent", category: "Debrief", defaultLLM: "openai/gpt-5.2", description: "Extracts conversation insights" },
  { id: "stage_progression", name: "StageProgressionAgent", category: "Debrief", defaultLLM: "openai/gpt-5.2", description: "Tracks stage progression" },
  { id: "relationship_health", name: "RelationshipHealthAgent", category: "Debrief", defaultLLM: "openai/gpt-5.2", description: "Assesses relationship health" },
  { id: "debrief_prompt", name: "DebriefPromptAgent", category: "Debrief", defaultLLM: "openai/gpt-5.2", description: "Generates debrief prompts" },
  { id: "learnings_store", name: "LearningsStoreAgent", category: "Debrief", defaultLLM: "openai/gpt-4o-mini", description: "Stores learnings" },
  
  // Escalation Agents
  { id: "signal_extractor", name: "SignalExtractorAgent", category: "Escalation", defaultLLM: "openai/gpt-4o-mini", description: "Extracts conversation signals" },
  { id: "language_signal", name: "LanguageSignalExtractorAgent", category: "Escalation", defaultLLM: "openai/gpt-4o-mini", description: "Extracts language signals" },
  { id: "stage_classifier", name: "StageClassifierAgent", category: "Escalation", defaultLLM: "openai/gpt-4o-mini", description: "Classifies escalation stage" },
  
  // Game Agents
  { id: "game_state", name: "GameStateAgent", category: "Game", defaultLLM: "openai/gpt-5.2", description: "Manages game state" },
  { id: "scoring", name: "ScoringAgent", category: "Game", defaultLLM: "openai/gpt-4o-mini", description: "Calculates scores" },
  { id: "game_persona", name: "GamePersonaAgent", category: "Game", defaultLLM: "openai/gpt-5.2", description: "Generates game personas" },
];

// LLM Function Definitions (global functions with configurable LLMs)
const LLM_FUNCTIONS = [
  { 
    function_key: "persona_chat_flow", 
    function_name: "Persona Chat Flow", 
    agent_name: "PersonaPromptBuilderAgent",
    prompt_key: "persona_full_template",
    default_llm: { provider: "anthropic", model: "claude-sonnet-4-5-20250929" },
    description: "Main persona roleplay LLM response (/api/v2/persona/chat)"
  },
  { 
    function_key: "advice_chat_flow", 
    function_name: "Agent Eros Coach", 
    agent_name: "advice_chat",
    prompt_key: "advice_direct",
    default_llm: { provider: "anthropic", model: "claude-sonnet-4-5-20250929" },
    description: "Advice chat and coaching conversations (/api/ai/chat)"
  },
  { 
    function_key: "compatibility_analysis", 
    function_name: "Compatibility Analyzer", 
    agent_name: "compatibility_analysis",
    prompt_key: "compatibility_analysis",
    default_llm: { provider: "openai", model: "gpt-5.2" },
    description: "Analyzes relationship compatibility using CORE data"
  },
  { 
    function_key: "generate_style_card_full", 
    function_name: "Generate STYLE CARD COMPLETE", 
    agent_name: "style_card_full_gen",
    prompt_key: "style_card_full",
    default_llm: { provider: "openai", model: "gpt-5.2" },
    description: "Generates comprehensive roleplay-ready style cards from communication samples"
  },
  { 
    function_key: "learning_profile_gen", 
    function_name: "Learning Library Generator", 
    agent_name: "learning_profile_gen",
    prompt_key: "learning_profile_generator",
    default_llm: { provider: "openai", model: "gpt-4o-mini" },
    description: "Generates 16 learning profile categories from data sources"
  },
  { 
    function_key: "persona_generator", 
    function_name: "AI Persona Generator", 
    agent_name: "persona_generator",
    prompt_key: "persona_generator",
    default_llm: { provider: "gemini", model: "gemini-3-flash-preview" },
    description: "Generates complete persona profiles from user descriptions with seed/mesh blending"
  },
  { 
    function_key: "chat_digest_memory", 
    function_name: "Chat Digest Memory", 
    agent_name: "chat_digest_memory",
    prompt_key: "chat_digest_memory",
    default_llm: { provider: "gemini", model: "gemini-3-flash-preview" },
    description: "Session Digest Routine — post-session structured memory updates (relational claims, timeline events, stage signal, outbound copy outcomes). Runs after a 60s debounce on session close."
  },
  { 
    function_key: "perspective_seeder", 
    function_name: "Perspective Seeder", 
    agent_name: "perspective_seeder",
    prompt_key: "perspective_seeder",
    default_llm: { provider: "gemini", model: "gemini-3-flash-preview" },
    description: "Free-text notes → structured relational_state claims. Powers the 'Seed from notes' flow on the Brain Sheet → Claims tab (Phase 5b)."
  },
  {
    function_key: "seed_bio_extractor",
    function_name: "Seed Bio Extractor",
    agent_name: "seed_bio_extractor",
    prompt_key: "seed_bio_extractor",
    default_llm: { provider: "gemini", model: "gemini-3-flash-preview" },
    description: "Wizard seed notes → bio_claim_actions[] for both user_persona and match_persona bio fields. Fires fire-and-forget after the wizard commit. Lands pending rows in db.bio_claim_actions → Bio Updates review panel. Same structured-JSON shape as perspective_seeder; defaults to Gemini Flash for speed/cost parity. Flip to Claude here if you want richer narrative-for-enum compliance."
  },
];

// Small color-coded chip showing what % of input on the last 100 calls
// for a given prompt_id came from Anthropic's prompt cache (cache_read).
// Helps spot drift: a healthy layered prompt sits at 70%+; a cache miss
// pattern (interleaved dynamic data, wrong provider, expired markers)
// shows 0-20%. Gray when no recent activity to score.
const CacheStatsBadge = ({ stats }) => {
  if (!stats || stats.sample_size === 0) {
    return (
      <span
        className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-white/5 text-gray-500 border border-white/10"
        title="No recent calls"
        data-testid="cache-stats-badge-empty"
      >
        — cache
      </span>
    );
  }
  const pct = Math.round((stats.cache_read_ratio || 0) * 100);
  // 0 reads + 0 writes for a provider that doesn't support caching
  // (e.g. Gemini) → grey "n/a" rather than alarmist red.
  if (stats.cache_read_tokens === 0 && stats.cache_write_tokens === 0) {
    return (
      <span
        className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-white/5 text-gray-500 border border-white/10"
        title={`${stats.sample_size} recent calls, no cache_control headers detected (provider may not support prompt caching)`}
        data-testid="cache-stats-badge-na"
      >
        n/a cache
      </span>
    );
  }
  let cls = "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
  if (pct < 25) cls = "bg-rose-500/15 text-rose-300 border-rose-500/30";
  else if (pct < 60) cls = "bg-amber-500/15 text-amber-300 border-amber-500/30";
  const tooltip = (
    `cache_read: ${stats.cache_read_tokens.toLocaleString()} tk / ` +
    `cache_write: ${stats.cache_write_tokens.toLocaleString()} tk / ` +
    `total input: ${stats.total_input_tokens.toLocaleString()} tk over ` +
    `${stats.sample_size} recent calls. ` +
    `${stats.calls_with_cache_read}/${stats.sample_size} calls hit cache.`
  );
  return (
    <span
      className={`px-1.5 py-0.5 rounded text-[10px] font-mono border ${cls}`}
      title={tooltip}
      data-testid={`cache-stats-badge-${pct < 25 ? "low" : pct < 60 ? "mid" : "high"}`}
    >
      {pct}% cache
    </span>
  );
};





export default function LLMMonitor() {
  const [rateLimiterHealth, setRateLimiterHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await authAxios.get("/admin/rate-limiter/health");
        if (!cancelled) setRateLimiterHealth(r.data);
      } catch (_) { /* silent */ }
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);
  const navigate = useNavigate();
  const [links, setLinks] = useState(DEFAULT_LINKS);
  const [editingKey, setEditingKey] = useState(null);
  const [editUrl, setEditUrl] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Agent configuration state
  const [agentAssignments, setAgentAssignments] = useState({});
  const [configTitle, setConfigTitle] = useState("");
  const [configNotes, setConfigNotes] = useState("");
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Speed test state
  const [speedTestModel, setSpeedTestModel] = useState("openai/gpt-4o-mini");
  const [speedTestPrompt, setSpeedTestPrompt] = useState("Write a haiku about artificial intelligence.");
  const [speedTestRunning, setSpeedTestRunning] = useState(false);
  const [speedTestResults, setSpeedTestResults] = useState([]);
  const [runAllModels, setRunAllModels] = useState(false);

  // LLM Function Assignments state
  const [functionAssignments, setFunctionAssignments] = useState({});
  const [savingFunctions, setSavingFunctions] = useState(false);

  // Cache hit-rate stats per prompt_id (last 100 calls each)
  const [cacheStats, setCacheStats] = useState({});

  // LLM Call Logs state
  const [selectedLlmProvider, setSelectedLlmProvider] = useState("anthropic");

  useEffect(() => {
    fetchLinks();
    fetchAgentConfigs();
    fetchFunctionAssignments();
    fetchCacheStats();
    // Initialize with defaults
    const defaults = {};
    AGENT_DEFAULTS.forEach(agent => {
      defaults[agent.id] = "default";
    });
    setAgentAssignments(defaults);
  }, []);

  const fetchLinks = async () => {
    try {
      const response = await authAxios.get("/admin/llm-monitor-links");
      if (response.data && Object.keys(response.data).length > 0) {
        setLinks({ ...DEFAULT_LINKS, ...response.data });
      }
    } catch (error) {
      console.log("Using default LLM monitor links");
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentConfigs = async () => {
    try {
      const response = await authAxios.get("/admin/agent-configs");
      setSavedConfigs(response.data || []);
    } catch (error) {
      console.log("No saved agent configs");
    }
  };

  const fetchFunctionAssignments = async () => {
    try {
      const response = await authAxios.get("/admin/llm-function-assignments");
      if (response.data && response.data.assignments) {
        setFunctionAssignments(response.data.assignments);
      } else {
        // Initialize with defaults
        const defaults = {};
        LLM_FUNCTIONS.forEach(fn => {
          defaults[fn.function_key] = "default";
        });
        setFunctionAssignments(defaults);
      }
    } catch (error) {
      console.log("No saved function assignments, using defaults");
      const defaults = {};
      LLM_FUNCTIONS.forEach(fn => {
        defaults[fn.function_key] = "default";
      });
      setFunctionAssignments(defaults);
    }
  };

  const fetchCacheStats = async () => {
    try {
      const promptIds = LLM_FUNCTIONS.map(fn => fn.prompt_key).join(",");
      const response = await authAxios.get(
        `/admin/plog/cache-stats?prompt_ids=${promptIds}&limit_per_prompt=100`
      );
      if (response.data && response.data.stats) {
        setCacheStats(response.data.stats);
      }
    } catch (error) {
      console.log("Cache stats unavailable", error);
    }
  };

  const handleFunctionAssignment = (functionKey, value) => {
    setFunctionAssignments(prev => ({
      ...prev,
      [functionKey]: value
    }));
  };

  const handleSaveFunctionAssignments = async () => {
    setSavingFunctions(true);
    try {
      await authAxios.post("/admin/llm-function-assignments", {
        assignments: functionAssignments
      });
      toast.success("Function assignments saved!");
    } catch (error) {
      toast.error("Failed to save function assignments");
      console.error(error);
    } finally {
      setSavingFunctions(false);
    }
  };

  const startEditing = (key) => {
    setEditingKey(key);
    setEditUrl(links[key].url);
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditUrl("");
  };

  const saveLink = async (key) => {
    try {
      const updatedLinks = {
        ...links,
        [key]: { ...links[key], url: editUrl }
      };
      
      await authAxios.put("/admin/llm-monitor-links", updatedLinks);
      setLinks(updatedLinks);
      setEditingKey(null);
      setEditUrl("");
      toast.success(`${links[key].name} link updated`);
    } catch (error) {
      toast.error("Failed to save link");
    }
  };

  const openLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const getIcon = (iconType) => {
    switch (iconType) {
      case "database":
        return <Database className="h-6 w-6" />;
      case "activity":
        return <Activity className="h-6 w-6" />;
      default:
        return <ExternalLink className="h-6 w-6" />;
    }
  };

  const handleAgentAssignment = (agentId, value) => {
    setAgentAssignments(prev => ({
      ...prev,
      [agentId]: value
    }));
  };

  const handleSaveConfig = async () => {
    if (!configTitle.trim()) {
      toast.error("Please enter a title for this configuration");
      return;
    }

    setSavingConfig(true);
    try {
      const configData = {
        title: configTitle,
        notes: configNotes,
        assignments: agentAssignments,
        created_at: new Date().toISOString()
      };

      await authAxios.post("/admin/agent-configs", configData);
      toast.success("Configuration saved!");
      setConfigTitle("");
      setConfigNotes("");
      fetchAgentConfigs();
    } catch (error) {
      toast.error("Failed to save configuration");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleLoadConfig = async (config) => {
    setAgentAssignments(config.assignments);
    setConfigTitle(config.title);
    setConfigNotes(config.notes || "");
    toast.success(`Loaded: ${config.title}`);
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm("Delete this configuration?")) return;
    
    try {
      await authAxios.delete(`/admin/agent-configs/${configId}`);
      toast.success("Configuration deleted");
      fetchAgentConfigs();
    } catch (error) {
      toast.error("Failed to delete configuration");
    }
  };

  const getAssignmentSummary = (assignments) => {
    const summary = [];
    const nonDefault = Object.entries(assignments).filter(([_, v]) => v !== "default");
    
    if (nonDefault.length === 0) return "All defaults";
    
    // Group by model
    const byModel = {};
    nonDefault.forEach(([agentId, modelValue]) => {
      const agent = AGENT_DEFAULTS.find(a => a.id === agentId);
      const model = AVAILABLE_MODELS.find(m => m.value === modelValue);
      if (agent && model) {
        if (!byModel[model.label]) byModel[model.label] = [];
        byModel[model.label].push(agent.name.replace("Agent", ""));
      }
    });

    return Object.entries(byModel)
      .slice(0, 3)
      .map(([model, agents]) => `${agents[0]}→${model}`)
      .join(", ") + (nonDefault.length > 3 ? "..." : "");
  };

  // Speed test functions
  const runSpeedTest = async (modelValue) => {
    const model = AVAILABLE_MODELS.find(m => m.value === modelValue);
    if (!model || model.value === "default") {
      toast.error("Please select a valid model");
      return null;
    }

    const startTime = performance.now();
    
    try {
      const response = await authAxios.post("/admin/llm-speed-test", {
        provider: model.provider,
        model: model.model,
        prompt: speedTestPrompt
      });
      
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);
      
      return {
        model: model.label,
        modelValue: modelValue,
        latency: latency,
        tokens: response.data.tokens || 0,
        response: response.data.response || "",
        tokensPerSecond: response.data.tokens ? Math.round((response.data.tokens / latency) * 1000) : 0,
        success: true,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      const endTime = performance.now();
      return {
        model: model.label,
        modelValue: modelValue,
        latency: Math.round(endTime - startTime),
        tokens: 0,
        response: error.response?.data?.detail || error.message,
        tokensPerSecond: 0,
        success: false,
        timestamp: new Date().toISOString()
      };
    }
  };

  const handleRunSpeedTest = async () => {
    setSpeedTestRunning(true);
    
    if (runAllModels) {
      // Run all models sequentially
      const results = [];
      const testableModels = AVAILABLE_MODELS.filter(m => m.value !== "default");
      
      for (const model of testableModels) {
        toast.info(`Testing ${model.label}...`);
        const result = await runSpeedTest(model.value);
        if (result) {
          results.push(result);
          setSpeedTestResults([...results]);
        }
      }
      
      toast.success(`Completed ${results.length} speed tests`);
    } else {
      // Run single model
      const result = await runSpeedTest(speedTestModel);
      if (result) {
        setSpeedTestResults(prev => [result, ...prev].slice(0, 20));
        if (result.success) {
          toast.success(`${result.model}: ${result.latency}ms`);
        } else {
          toast.error(`${result.model} failed`);
        }
      }
    }
    
    setSpeedTestRunning(false);
  };

  const clearSpeedTestResults = () => {
    setSpeedTestResults([]);
  };

  const categories = ["all", ...new Set(AGENT_DEFAULTS.map(a => a.category))];
  const filteredAgents = selectedCategory === "all" 
    ? AGENT_DEFAULTS 
    : AGENT_DEFAULTS.filter(a => a.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-divine-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Rate Limiter Health */}
      {rateLimiterHealth && (
        <div className="bg-divine-charcoal/50 border border-divine-gold/20 rounded-xl p-5" data-testid="rate-limiter-health-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-divine-gold flex items-center gap-2">
                <Zap className="w-4 h-4" /> Rate Limiter Health
              </h2>
              <p className="text-xs text-gray-400 mt-1">
                Fast-path skips Redis for well-behaved traffic. Generic /api/* is memory-only.
              </p>
            </div>
            <div className={`px-3 py-1 rounded-full text-xs font-mono ${
              rateLimiterHealth.redis_available
                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-400/30"
                : rateLimiterHealth.redis_disabled_for_s > 0
                  ? "bg-amber-500/15 text-amber-200 border border-amber-400/30"
                  : "bg-white/5 text-white/50 border border-white/10"
            }`}>
              {rateLimiterHealth.redis_available
                ? "Redis: healthy"
                : rateLimiterHealth.redis_disabled_for_s > 0
                  ? `Redis: cooldown ${Math.round(rateLimiterHealth.redis_disabled_for_s)}s`
                  : rateLimiterHealth.redis_configured
                    ? "Redis: disabled"
                    : "Redis: not configured"}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="bg-divine-black/40 rounded-md p-3">
              <div className="text-[10px] uppercase text-white/40">Fast-path hits</div>
              <div className="text-emerald-300 font-mono text-lg">{rateLimiterHealth.fast_path_hits.toLocaleString()}</div>
              <div className="text-[10px] text-white/40 mt-0.5">No Redis touched</div>
            </div>
            <div className="bg-divine-black/40 rounded-md p-3">
              <div className="text-[10px] uppercase text-white/40">Memory bucket hits</div>
              <div className="text-white/80 font-mono text-lg">{rateLimiterHealth.memory_bucket_hits.toLocaleString()}</div>
              <div className="text-[10px] text-white/40 mt-0.5">Local counter used</div>
            </div>
            <div className="bg-divine-black/40 rounded-md p-3">
              <div className="text-[10px] uppercase text-white/40">Redis bucket hits</div>
              <div className="text-sky-300 font-mono text-lg">{rateLimiterHealth.redis_bucket_hits.toLocaleString()}</div>
              <div className="text-[10px] text-white/40 mt-0.5">
                {rateLimiterHealth.redis_calls_ok} ok / {rateLimiterHealth.redis_calls_failed} failed
              </div>
            </div>
            <div className="bg-divine-black/40 rounded-md p-3">
              <div className="text-[10px] uppercase text-white/40">Error rate</div>
              <div className={`font-mono text-lg ${
                rateLimiterHealth.redis_error_rate_pct > 5
                  ? "text-rose-300"
                  : rateLimiterHealth.redis_error_rate_pct > 1
                    ? "text-amber-300"
                    : "text-emerald-300"
              }`}>
                {rateLimiterHealth.redis_error_rate_pct}%
              </div>
              <div className="text-[10px] text-white/40 mt-0.5">
                Streak: {rateLimiterHealth.consecutive_errors}
              </div>
            </div>
          </div>
          {rateLimiterHealth.redis_last_error && (
            <div className="mt-3 text-[11px] text-rose-300/80 font-mono break-all">
              Last error: {rateLimiterHealth.redis_last_error}
            </div>
          )}
          <div className="mt-3 text-[10px] text-white/40">
            Coordinated buckets: {rateLimiterHealth.coordinated_buckets.join(", ")}
          </div>
        </div>
      )}

      {/* External Links Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-divine-gold">External Monitors</h2>
            <p className="text-sm text-gray-400 mt-1">
              External monitoring and observability tools
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {Object.entries(links).map(([key, link]) => (
            <div
              key={key}
              className="bg-divine-charcoal/50 border border-divine-gold/20 rounded-xl p-5 hover:border-divine-gold/40 transition-all"
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  key === "redis" 
                    ? "bg-red-500/20 text-red-400" 
                    : "bg-blue-500/20 text-blue-400"
                }`}>
                  {getIcon(link.icon)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-white">{link.name}</h3>
                  <p className="text-sm text-gray-400 mt-1">{link.description}</p>
                  
                  {editingKey === key ? (
                    <div className="mt-4 space-y-3">
                      <Input
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        placeholder="Enter URL"
                        className="bg-divine-black border-divine-gold/30 text-white text-sm"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => saveLink(key)}
                          className="bg-divine-gold text-divine-black hover:bg-divine-gold/80"
                        >
                          <Save className="h-4 w-4 mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelEditing}
                          className="text-gray-400 hover:text-white"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => openLink(link.url)}
                        className={`${
                          key === "redis"
                            ? "bg-red-500/20 text-red-400 hover:bg-red-500/30"
                            : "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                        }`}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Open
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEditing(key)}
                        className="text-gray-400 hover:text-white"
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* LLM Call Logs Panel */}
        <div className="mt-6 bg-divine-charcoal/50 border border-divine-gold/20 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-purple-500/20 text-purple-400">
              <FileText className="h-6 w-6" />
            </div>
            
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">LLM Call Logs</h3>
              <p className="text-sm text-gray-400 mt-1">
                View API call logs directly on your LLM provider's dashboard
              </p>
              
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <Select value={selectedLlmProvider} onValueChange={setSelectedLlmProvider}>
                  <SelectTrigger className="w-48 bg-divine-black border-divine-gold/30 text-white">
                    <SelectValue placeholder="Select Provider" />
                  </SelectTrigger>
                  <SelectContent className="bg-divine-black border-divine-gold/30">
                    {LLM_PROVIDER_DASHBOARDS.map(provider => (
                      <SelectItem 
                        key={provider.value} 
                        value={provider.value} 
                        className="text-white hover:bg-white/10"
                      >
                        <div className="flex items-center gap-2">
                          {provider.value === "anthropic" && <Brain className="w-4 h-4 text-orange-400" />}
                          {provider.value === "openai" && <Sparkles className="w-4 h-4 text-green-400" />}
                          {provider.value === "google" && <Cloud className="w-4 h-4 text-blue-400" />}
                          {provider.value === "deepseek" && <Cpu className="w-4 h-4 text-cyan-400" />}
                          {provider.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  size="sm"
                  onClick={() => {
                    const provider = LLM_PROVIDER_DASHBOARDS.find(p => p.value === selectedLlmProvider);
                    if (provider) openLink(provider.url);
                  }}
                  className={`${
                    selectedLlmProvider === "anthropic" ? "bg-orange-500/20 text-orange-400 hover:bg-orange-500/30" :
                    selectedLlmProvider === "openai" ? "bg-green-500/20 text-green-400 hover:bg-green-500/30" :
                    selectedLlmProvider === "deepseek" ? "bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30" :
                    "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                  }`}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Open {LLM_PROVIDER_DASHBOARDS.find(p => p.value === selectedLlmProvider)?.label.split(" ")[0]} Logs
                </Button>
              </div>

              {/* Quick links to all providers */}
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs text-gray-500">Quick links:</span>
                {LLM_PROVIDER_DASHBOARDS.map(provider => (
                  <button
                    key={provider.value}
                    onClick={() => openLink(provider.url)}
                    className={`text-xs px-2 py-1 rounded-full transition-colors ${
                      provider.value === "anthropic" ? "bg-orange-500/10 text-orange-400 hover:bg-orange-500/20" :
                      provider.value === "openai" ? "bg-green-500/10 text-green-400 hover:bg-green-500/20" :
                      provider.value === "deepseek" ? "bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20" :
                      "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                    }`}
                  >
                    {provider.label.split(" ")[0]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Speed Test Section */}
      <div className="border-t border-divine-gold/20 pt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-divine-gold flex items-center gap-2">
              <Zap className="w-5 h-5" />
              LLM Speed Test
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Benchmark response times across different models
            </p>
          </div>
        </div>

        <div className="bg-divine-charcoal/30 border border-divine-gold/20 rounded-xl p-5">
          {/* Test Configuration */}
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Model to Test</label>
              <Select value={speedTestModel} onValueChange={setSpeedTestModel} disabled={runAllModels}>
                <SelectTrigger className="bg-divine-black border-divine-gold/30 text-white">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent className="bg-divine-black border-divine-gold/30">
                  {AVAILABLE_MODELS.filter(m => m.value !== "default").map(model => (
                    <SelectItem key={model.value} value={model.value} className="text-white hover:bg-white/10">
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={runAllModels}
                  onChange={(e) => setRunAllModels(e.target.checked)}
                  className="w-4 h-4 rounded border-divine-gold/50 text-divine-gold focus:ring-divine-gold/50 bg-divine-black"
                />
                <span className="text-sm text-gray-400">Test all models</span>
              </label>
            </div>
          </div>

          <div className="mb-4">
            <label className="text-sm text-gray-400 mb-1 block">Test Prompt</label>
            <Textarea
              value={speedTestPrompt}
              onChange={(e) => setSpeedTestPrompt(e.target.value)}
              placeholder="Enter a test prompt..."
              className="bg-divine-black border-divine-gold/30 text-white min-h-[80px]"
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleRunSpeedTest}
              disabled={speedTestRunning}
              className="bg-green-600 hover:bg-green-500 text-white"
            >
              {speedTestRunning ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  {runAllModels ? "Run All Tests" : "Run Test"}
                </>
              )}
            </Button>
            {speedTestResults.length > 0 && (
              <Button
                variant="ghost"
                onClick={clearSpeedTestResults}
                className="text-gray-400 hover:text-white"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Results
              </Button>
            )}
          </div>
        </div>

        {/* Speed Test Results */}
        {speedTestResults.length > 0 && (
          <div className="mt-4 bg-divine-charcoal/30 border border-divine-gold/20 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-divine-gold/10 flex items-center justify-between">
              <h3 className="text-white font-medium flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-divine-gold" />
                Results ({speedTestResults.length})
              </h3>
              <div className="text-xs text-gray-500">
                Sorted by latency (fastest first when all models tested)
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-divine-black/50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-divine-gold uppercase">Model</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-divine-gold uppercase">Latency</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-divine-gold uppercase">Tokens</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-divine-gold uppercase">Tok/s</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-divine-gold uppercase">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-divine-gold uppercase">Response Preview</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divine-gold/10">
                  {[...speedTestResults]
                    .sort((a, b) => runAllModels ? a.latency - b.latency : 0)
                    .map((result, idx) => (
                    <tr key={idx} className="hover:bg-white/5">
                      <td className="px-4 py-3 text-white text-sm font-medium">{result.model}</td>
                      <td className="px-4 py-3">
                        <span className={`text-sm font-mono ${
                          result.latency < 1000 ? "text-green-400" :
                          result.latency < 3000 ? "text-yellow-400" :
                          "text-red-400"
                        }`}>
                          {result.latency}ms
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{result.tokens || "-"}</td>
                      <td className="px-4 py-3 text-gray-400 text-sm">{result.tokensPerSecond || "-"}</td>
                      <td className="px-4 py-3">
                        {result.success ? (
                          <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">Success</span>
                        ) : (
                          <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">Failed</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs max-w-[300px] truncate" title={result.response}>
                        {result.response?.slice(0, 100) || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* LLM Function Assignments Section */}
      <div className="border-t border-divine-gold/20 pt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-divine-gold flex items-center gap-2">
              <Settings2 className="w-5 h-5" />
              LLM Function Assignments
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Configure which LLM is assigned to different functions
            </p>
          </div>
        </div>

        <div className="bg-divine-charcoal/30 border border-divine-gold/20 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-divine-black/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-divine-gold uppercase">Function</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-divine-gold uppercase">Agent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-divine-gold uppercase">Prompt</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-divine-gold uppercase">Default LLM</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-divine-gold uppercase">Assign LLM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divine-gold/10">
                {LLM_FUNCTIONS.map((fn) => {
                  const defaultModel = AVAILABLE_MODELS.find(
                    m => m.provider === fn.default_llm.provider && m.model === fn.default_llm.model
                  );
                  return (
                    <tr key={fn.function_key} className="hover:bg-white/5">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white text-sm font-medium">{fn.function_name}</p>
                          <p className="text-gray-500 text-xs">{fn.description}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full font-mono">
                          {fn.agent_name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/admin?tab=prompts&agent=${fn.agent_name}`)}
                            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm underline"
                          >
                            <Link className="w-3 h-3" />
                            {fn.prompt_key}
                          </button>
                          <CacheStatsBadge stats={cacheStats[fn.prompt_key]} />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {defaultModel?.label || `${fn.default_llm.provider}/${fn.default_llm.model}`}
                      </td>
                      <td className="px-4 py-3">
                        <Select
                          value={functionAssignments[fn.function_key] || "default"}
                          onValueChange={(v) => handleFunctionAssignment(fn.function_key, v)}
                        >
                          <SelectTrigger className="w-52 bg-divine-black border-divine-gold/30 text-white text-sm h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-divine-black border-divine-gold/30">
                            {AVAILABLE_MODELS.map(model => (
                              <SelectItem key={model.value} value={model.value} className="text-white hover:bg-white/10 text-sm">
                                {model.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4">
          <Button
            onClick={handleSaveFunctionAssignments}
            disabled={savingFunctions}
            className="bg-divine-gold text-divine-black hover:bg-divine-gold/80"
          >
            {savingFunctions ? (
              <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Agent Configuration Section */}
      <div className="border-t border-divine-gold/20 pt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-divine-gold flex items-center gap-2">
              <Bot className="w-5 h-5" />
              Agent LLM Assignments
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Configure which LLM model each agent uses ({AGENT_DEFAULTS.length} agents)
            </p>
          </div>
          
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-40 bg-divine-black border-divine-gold/30 text-white">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent className="bg-divine-black border-divine-gold/30">
              {categories.map(cat => (
                <SelectItem key={cat} value={cat} className="text-white hover:bg-white/10 capitalize">
                  {cat === "all" ? "All Categories" : cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Configuration Title & Notes */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Configuration Title</label>
            <Input
              value={configTitle}
              onChange={(e) => setConfigTitle(e.target.value)}
              placeholder="e.g., Fast Config, Quality Config..."
              className="bg-divine-black border-divine-gold/30 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1 block">Notes</label>
            <Input
              value={configNotes}
              onChange={(e) => setConfigNotes(e.target.value)}
              placeholder="Description of this configuration..."
              className="bg-divine-black border-divine-gold/30 text-white"
            />
          </div>
        </div>

        {/* Save/Load Buttons */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={handleSaveConfig}
            disabled={savingConfig || !configTitle.trim()}
            className="bg-divine-gold text-divine-black hover:bg-divine-gold/80"
          >
            {savingConfig ? (
              <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Configuration
          </Button>
        </div>

        {/* Agent Table */}
        <div className="bg-divine-charcoal/30 border border-divine-gold/20 rounded-xl overflow-hidden">
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full">
              <thead className="bg-divine-black/50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-divine-gold uppercase">Agent</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-divine-gold uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-divine-gold uppercase">Default LLM</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-divine-gold uppercase">Assign LLM</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divine-gold/10">
                {filteredAgents.map((agent) => {
                  const defaultModel = AVAILABLE_MODELS.find(m => m.value === agent.defaultLLM);
                  return (
                    <tr key={agent.id} className="hover:bg-white/5">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white text-sm font-medium">{agent.name}</p>
                          <p className="text-gray-500 text-xs">{agent.description}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                          {agent.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {defaultModel?.label || agent.defaultLLM}
                      </td>
                      <td className="px-4 py-3">
                        <Select
                          value={agentAssignments[agent.id] || "default"}
                          onValueChange={(v) => handleAgentAssignment(agent.id, v)}
                        >
                          <SelectTrigger className="w-48 bg-divine-black border-divine-gold/30 text-white text-sm h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-divine-black border-divine-gold/30">
                            {AVAILABLE_MODELS.map(model => (
                              <SelectItem key={model.value} value={model.value} className="text-white hover:bg-white/10 text-sm">
                                {model.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Saved Configurations Section */}
      {savedConfigs.length > 0 && (
        <div className="border-t border-divine-gold/20 pt-8">
          <h3 className="text-lg font-semibold text-divine-gold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Saved Configurations
          </h3>
          
          <div className="space-y-3">
            {savedConfigs.map((config) => (
              <div
                key={config.config_id}
                className="bg-divine-charcoal/30 border border-divine-gold/20 rounded-lg p-4 flex items-center justify-between hover:border-divine-gold/40 transition-all"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h4 className="text-white font-medium">{config.title}</h4>
                    <span className="text-gray-500 text-xs">
                      {new Date(config.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  {config.notes && (
                    <p className="text-gray-400 text-sm mt-1">{config.notes}</p>
                  )}
                  <p className="text-gray-500 text-xs mt-2 font-mono">
                    {getAssignmentSummary(config.assignments)}
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleLoadConfig(config)}
                    className="bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Load
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteConfig(config.config_id)}
                    className="text-red-400 hover:bg-red-400/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Reference */}
      <div className="bg-divine-charcoal/30 border border-divine-gold/10 rounded-lg p-4">
        <h4 className="text-sm font-medium text-divine-gold mb-2">Quick Reference</h4>
        <ul className="text-xs text-gray-400 space-y-1">
          <li>• <strong>Default:</strong> Uses the system's hardcoded LLM for each agent</li>
          <li>• <strong>Claude Sonnet 4:</strong> Best for roleplay, persona responses (slower, higher quality)</li>
          <li>• <strong>GPT-5.2:</strong> Best for analysis, planning, coaching (balanced)</li>
          <li>• <strong>GPT-4o-mini:</strong> Best for quick tasks, classification (fast, cheaper)</li>
          <li>• <strong>Groq:</strong> Ultra-fast inference, good for high-volume scenarios</li>
        </ul>
      </div>
    </div>
  );
}
